import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Sentry } from '@/lib/sentry'
import { routes } from '@/shared/constants'
import { Confeti } from './Confeti'
import { NovedadCuerpo } from './NovedadCuerpo'
import { PortadaProducto } from './PortadaProducto'
import { esFalloReportable, marcarNovedadesVistas } from './novedades'
import { useNovedadesAlEntrar } from './useNovedadesAlEntrar'

/**
 * El aviso que cuenta lo nuevo al entrar al portal (goal novedades-del-portal
 * 2). **Se superpone** a lo que el usuario venía a hacer: el índice de `/app`
 * sigue llevando a `/app/businesses`, sin pantalla intermedia.
 *
 * 🚨 Se monta en DOS sitios (goal portal-correcciones 10): en `AppShell`
 * —dentro de `RequireAuth`, así que siempre hay sesión— y en la portada `/`
 * a través de `NovedadesEnLaPortada`, que es quien pone allí la guarda de
 * sesión que aquí da `RequireAuth`. Los dos árboles son ramas hermanas del
 * router: **nunca renderizan a la vez**, así que no hay dos peticiones ni dos
 * modales. Este componente no sabe en cuál de los dos está, y no debe saberlo.
 *
 * 🚨 Quién lo ha visto lo decide el SERVIDOR por el `sub` del token, no
 * `localStorage`: guardarlo en el navegador haría que la misma persona lo
 * recibiera otra vez en otro equipo.
 *
 * 🚨 La marca de «visto» es efecto del CIERRE, nunca del render. Se cierra de
 * tres formas —la equis, «Entendido» y `Esc`—, más los enlaces de detalles, y
 * todas pasan por `cerrar()`. Si el POST falla, se cierra igual: volverá a
 * salir la próxima vez, que es el fallo barato; el caro sería quemar la novedad
 * sin que nadie la leyera.
 */
export function AvisoDeNovedades() {
  const { t } = useTranslation()
  const estadoNovedades = useNovedadesAlEntrar()
  const [cerrado, setCerrado] = useState(false)
  // Ref y no estado: el POST se manda UNA vez aunque el cierre llegue por dos
  // caminos a la vez (pulsar «Ver todo» dispara también el onOpenChange).
  const yaMarcado = useRef(false)

  const hayNovedades =
    estadoNovedades.estado === 'listo' &&
    estadoNovedades.datos.pendiente &&
    estadoNovedades.datos.novedades.length > 0
  const abierto = hayNovedades && !cerrado

  const cerrar = () => {
    setCerrado(true)
    if (yaMarcado.current) return
    yaMarcado.current = true
    void marcarNovedadesVistas().catch((error: unknown) => {
      // El aviso ya se cerró. Sin marca de agua volverá a salir la próxima vez
      // —el fallo barato—, pero si esto empieza a fallar de verdad el aviso se
      // vuelve insistente y nadie lo sabría: por eso se reporta lo que no es
      // esperado.
      if (esFalloReportable(error)) {
        Sentry.captureException(error, {
          tags: { operation: 'POST /novedades/visto' },
        })
      }
    })
  }

  if (!hayNovedades) return null
  const { novedades } = estadoNovedades.datos

  return (
    <>
      {/* El confeti va FUERA del Dialog y se pinta en su propio portal sobre
          <body> (ver Confeti.tsx): el contenido de Radix recorta, y una lluvia
          a pantalla completa no es contenido del diálogo. */}
      <Confeti activo={abierto} />
      <Dialog
        open={abierto}
        onOpenChange={(siguiente) => {
          if (!siguiente) cerrar()
        }}
      >
        {/* Sin `id` propio ni `aria-labelledby` a mano: el cableado del nombre
            accesible lo hace Radix entre `DialogContent` y `DialogTitle`, y
            pisarle el `id` al título rompe su comprobación —«DialogContent
            requires a DialogTitle»— aunque el título esté ahí (visto en
            navegador, 2026-08-31). */}
        <DialogContent className="flex max-h-[85vh] max-w-[600px] flex-col gap-0 overflow-hidden p-0">
          <div className="px-[26px] pb-4 pt-[26px]">
            <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand">
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              {t('novedades.badge')}
            </span>
            <DialogTitle className="mb-1.5 mt-3 font-sans text-2xl font-bold tracking-tight">
              {t('novedades.tituloAviso')}
            </DialogTitle>
            <DialogDescription className="max-w-[46ch] text-[13.5px]">
              {t('novedades.subtituloAviso')}
            </DialogDescription>
          </div>

          <div className="flex flex-col overflow-y-auto px-[26px] pb-2">
            {novedades.map((novedad) => (
              <article
                key={`${novedad.repo}-${novedad.version}`}
                className="flex items-start gap-4 border-t border-line py-[17px] first:border-t-0 first:pt-2"
              >
                <PortadaProducto product={novedad.product} />
                <div className="min-w-0 flex-1">
                  <NovedadCuerpo novedad={novedad} onDetalles={cerrar} />
                </div>
              </article>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-[26px] pb-5 pt-4">
            <p className="m-0 text-xs text-ink-subtle">
              {t('novedades.pieAviso')}{' '}
              <Link
                to={routes.app.novedades}
                onClick={cerrar}
                className="text-ink-muted underline underline-offset-2"
              >
                {t('novedades.tituloPagina')}
              </Link>
              .
            </p>
            <div className="flex items-center gap-2">
              <Link to={routes.public.releases} onClick={cerrar}>
                <Button variant="outline" size="md" type="button">
                  {t('novedades.verTodo')}
                </Button>
              </Link>
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={cerrar}
                autoFocus
              >
                {t('novedades.entendido')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
